/**
 * A class's study plans, in the admin console.
 *
 * PLURAL now: a class may hold several named plans — "Steady", "Catch-up from
 * October" — and students pick between them. The picker and editor are both
 * shared with the student app (`@shared-ui/class-shared`), which call the same
 * routes, so a second copy would only have drifted.
 *
 * The console is the repair path the democratic rule lacks — and with
 * `student_edits_class_records` off it is the ONLY surface that can set a class
 * plan at all, since the student editor is the sole other one. It is also the only
 * surface that may publish a class's plan beyond the class: a room owns its plan
 * collectively, so no ONE member gets to hand it to strangers.
 */
import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSubjectLabel } from '@shared-ui/reference-data';
import { PlansPanel } from '@shared-ui/class-shared';
import { curriculumApi } from '../../api/curriculumApi';
import { classSharedApi, type SchoolClass } from '../../api/schoolsApi';
import { useChaptersFor } from './useChaptersFor';

export function PlanTab({ cls, subjects }: { cls: SchoolClass; subjects: string[] }) {
  const subjectLabel = useSubjectLabel();

  const { data: curricula } = useQuery({
    queryKey: ['curricula'],
    queryFn: () => curriculumApi.listCurricula(),
    enabled: !!cls.board,
  });

  /** The class's published syllabus for one subject — teacher-owned and
   *  inactive copies excluded, exactly as the Coverage tab resolves it, so the
   *  two tabs cannot disagree about which book the class follows. */
  const curriculumFor = useCallback((s: string) => {
    if (!cls.board) return undefined;
    return (curricula?.curricula ?? []).find(c =>
      c.board === cls.board
      && String(c.grade ?? '') === String(cls.grade)
      && c.subject === s
      && c.ownerType !== 'teacher'
      && c.isActive !== false);
  }, [curricula, cls.board, cls.grade]);

  /**
   * Every subject this class teaches, with the syllabus each follows.
   *
   * A plan covers a SET of subjects now, so this is the menu a plan is built
   * from rather than a filter on which plan is shown.
   */
  const availableSubjects = useMemo(
    // EVERY subject the class teaches, including any with no syllabus resolved.
    // Filtering those out hid subjects the class actually runs; a subject without
    // a curriculum is still plannable — it carries the shared pass dates and
    // simply has no chapters to date.
    () => subjects.map(sub => ({ subject: sub, curriculumId: curriculumFor(sub)?.id })),
    [subjects, curriculumFor],
  );

  /** Chapters for EVERY subject the class teaches, not just one — shared with
   *  the platform's plan tab so the two cannot fetch them differently. */
  const chaptersFor = useChaptersFor(availableSubjects);

  /**
   * The plans WE ship, listed under the class's own.
   *
   * A class choosing how to run its year should see the platform's plans beside
   * its own — that is the shelf the question is answered from. They are read-only
   * here: the class does not own them, and editing one from a class tab would
   * change it for every other class too.
   */
  const { data: ours } = useQuery({
    queryKey: ['study-plans', 'platform'],
    queryFn: () => classSharedApi.listPlans({ ownerKind: 'platform' }),
  });
  const platformPlans = useMemo(
    // Only the ones a class can actually use: a private draft of ours is not on
    // the shelf yet, and one written for another level names other chapters.
    () => (ours?.plans ?? []).filter(p =>
      p.visibility === 'listed'
      && (!p.board || !cls.board || p.board === cls.board)
      && (!p.grade || p.grade === String(cls.grade ?? ''))),
    [ours, cls.board, cls.grade],
  );

  /** The levels a plan may be written for, from the syllabuses that exist. */
  const boardGradeOptions = useMemo(() => {
    const byBoard = new Map<string, Set<string>>();
    for (const c of curricula?.curricula ?? []) {
      if (c.ownerType === 'teacher' || c.isActive === false || !c.board || !c.grade) continue;
      byBoard.set(c.board, (byBoard.get(c.board) ?? new Set()).add(String(c.grade)));
    }
    return [...byBoard.entries()]
      .map(([board, grades]) => ({ board, grades: [...grades].sort((a, b) => Number(a) - Number(b)) }))
      .sort((a, b) => a.board.localeCompare(b.board));
  }, [curricula]);
  if (subjects.length === 0) {
    return (
      <section className="school-section">
        <p className="school-hint">
          This class has not said which subjects it teaches, so there is nothing to plan.
          Set them on the General tab first — a plan covers a set of subjects, and guessing
          them would put a curriculum in front of the class that nobody chose.
        </p>
      </section>
    );
  }

  if (!cls.board) {
    return (
      <section className="school-section">
        <p className="school-hint">
          This class has no board set, so it has no syllabus to plan against. Set the board on
          the General tab first.
        </p>
      </section>
    );
  }

  return (
    <section className="school-section">
      <PlansPanel
        api={classSharedApi}
        ownerKind="schoolClass"
        ownerId={cls.id}
        // A class already knows its board and grade; stamping them lets a plan
        // resolve the syllabus for a subject added later on its own.
        boardGrade={{ board: cls.board, grade: String(cls.grade ?? '') }}
        subjectName={subjectLabel}
        availableSubjects={availableSubjects}
        chaptersFor={chaptersFor}
        additionalPlans={platformPlans}
        // Ours are on the shelf to be READ from here, never edited: one document
        // serves every class.
        canEditPlan={(p) => p.ownerKind === 'schoolClass'}
        // The console is the surface that may hand a class's plan to strangers —
        // no one member of a room gets to, which is why the server refuses it for
        // everyone but an admin.
        canPublishPlan={(p) => p.ownerKind === 'schoolClass'}
        boardGradeOptions={boardGradeOptions}
      />
    </section>
  );
}
